import { useApi, usd } from "../../dashboard/api";
import { AccountMetadata } from "../../components/dashboard/AccountMetadata";
import { ActivityFeed } from "../../components/dashboard/ActivityFeed";
import { FinancialOverview } from "../../components/dashboard/FinancialOverview";
import { MatrixOverview } from "../../components/dashboard/MatrixOverview";
import { QuickActions } from "../../components/dashboard/QuickActions";
import { TrendChart } from "../../components/dashboard/TrendChart";
import { ErrorState, Panel, Skeleton } from "../../components/dashboard/ui";

const Metric = ({ label, value, testId }) => (
    <div className="px-4 py-4 lg:px-5" data-testid={testId}>
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">{label}</p>
        <p className="mt-1 text-base font-extrabold text-nx-text tabular">{value}</p>
    </div>
);

export const DashboardPage = () => {
    const { data, loading, error } = useApi("summary");
    const { data: matrix } = useApi("matrix");

    if (loading) {
        return (
            <div data-testid="dashboard-loading">
                <Skeleton className="h-8 w-40" />
                <Skeleton className="mt-6 h-40 w-full rounded-xl" />
                <Skeleton className="mt-6 h-12 w-full rounded-xl" />
                <Skeleton className="mt-6 h-28 w-full rounded-xl" />
            </div>
        );
    }
    if (error || !data) {
        return <ErrorState testId="dashboard-error" />;
    }

    const { user, financial, platform } = data;

    return (
        <div data-testid="dashboard-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-nx-mute">Welcome back</p>
                    <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-nx-text" data-testid="welcome-name">
                        {user.name}
                    </h2>
                    <p className="mt-1 text-xs text-nx-mute">Your account overview and latest ecosystem activity.</p>
                </div>
                <div className="flex items-center gap-2.5 rounded-lg border border-white/5 bg-nx-alt px-3.5 py-2" data-testid="current-level-chip">
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">Current Level</span>
                    <span className="rounded-md border border-nx-lime/30 bg-nx-lime/10 px-2 py-0.5 text-xs font-extrabold text-nx-lime">
                        LEVEL {user.level}
                    </span>
                </div>
            </div>

            <Panel className="mt-6" testId="account-overview" padded={false}>
                <div className="grid lg:grid-cols-12">
                    <div className="p-5 sm:p-6 lg:col-span-7 lg:border-r lg:border-white/5">
                        <div className="flex items-center gap-3">
                            <img src="/assets/nexora-eagle.png" alt="" className="h-7 w-auto" />
                            <div>
                                <p className="text-sm font-extrabold tracking-wide text-nx-text">{user.name}</p>
                                <p className="mt-0.5 font-mono text-[11px] text-nx-mute" data-testid="account-identity">
                                    ID {user.accountId} · Upline #{user.upline}
                                </p>
                            </div>
                            <span className="ms-auto rounded-md border border-nx-lime/30 bg-nx-lime/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-nx-lime">
                                Level {user.level}
                            </span>
                        </div>
                        <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.2em] text-nx-mute">Total Profit</p>
                        <p className="mt-2 text-5xl font-extrabold tracking-tight text-nx-text tabular" data-testid="total-profit">
                            {usd(financial.totalProfit)}
                        </p>
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-white/5 border-t border-white/5 lg:col-span-5 lg:grid-cols-1 lg:divide-x-0 lg:divide-y lg:border-t-0">
                        <Metric label="Profit Ratio" value={`${financial.profitRatio}%`} testId="metric-ratio" />
                        <Metric label="Partners" value={financial.partners} testId="metric-partners" />
                        <Metric label="Total Team" value={financial.totalTeam} testId="metric-team" />
                    </div>
                </div>
            </Panel>

            <QuickActions accountId={user.accountId} />
            <AccountMetadata wallet={user.wallet} joinDate={user.joinDate} network={user.network} />
            <FinancialOverview financial={financial} />
            <TrendChart />
            {matrix && <MatrixOverview matrix={matrix} />}
            <ActivityFeed limit={6} showSummary summary={platform} />
        </div>
    );
};
